/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/auth";
import { useLanguage } from "@/hooks/useLanguage";
import Widget, { WidgetSettings } from "@/components/widgets";
import { Reorder, motion, AnimatePresence } from "framer-motion";
import type {
  WidgetType,
  WidgetInfo,
  WidgetSize,
  ColorScheme,
} from "@/components/widgets/widget-declarations";
import "@/styles/widget-spacing.css";

// مخططات الألوان للخلفية بصور
const colorSchemes: ColorScheme[] = [
  {
    id: "yellow",
    name: "ذهبي",
    dotColor: "#FFC700",
  },
  {
    id: "orange",
    name: "برتقالي",
    dotColor: "#FF7A00",
  },
  {
    id: "red",
    name: "أحمر",
    dotColor: "#FF3A00",
  },
  {
    id: "green",
    name: "أخضر",
    dotColor: "#00B066",
  },
  {
    id: "purple",
    name: "بنفسجي",
    dotColor: "#7B00D3",
  },
  {
    id: "blue",
    name: "أزرق",
    dotColor: "#0077E6",
  },
];

const UserWidgetsDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [widgets, setWidgets] = useState<WidgetInfo[]>([]);
  const [availableWidgets, setAvailableWidgets] = useState<WidgetInfo[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedWidget, setSelectedWidget] = useState<WidgetInfo | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [colorScheme, setColorScheme] = useState<string>("blue");
  const [showColorSelector, setShowColorSelector] = useState(false);

  // للتحقق من حجم الشاشة
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  // استماع لتغيير حجم الشاشة
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // تنفيذ مرة للتأكد من القيمة الصحيحة
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // تعديل حجم الويدجت للشاشات الصغيرة
  useEffect(() => {
    if (screenSize.width < 640) {
      // تغيير حجم الويدجت للشاشات الصغيرة إلى small
      const widgetsToUpdate = (currentWidgets: WidgetInfo[]) =>
        currentWidgets.map((widget: WidgetInfo) => {
          // للشاشات الصغيرة جداً (<480px)، تصغير جميع الويدجت
          if (screenSize.width < 480 && widget.size !== "small") {
            return { ...widget, size: "small" as WidgetSize };
          }
          // للشاشات المتوسطة (<640px)، تصغير ويدجت المستخدم فقط
          else if (
            screenSize.width < 640 &&
            widget.type === "user" &&
            widget.size !== "small"
          ) {
            return { ...widget, size: "small" as WidgetSize };
          }
          return widget;
        });

      setWidgets(widgetsToUpdate);
    }
  }, [screenSize.width]);

  // مؤثر لإعداد الويدجت الافتراضية
  useEffect(() => {
    // البيانات الافتراضية للويدجت المناسبة للمستخدم العادي
    // Make sure there's exactly one user widget
    const defaultWidgets: WidgetInfo[] = [
      {
        id: "9",
        type: "user",
        title: "بطاقة المستخدم",
        icon: "/img/widgets/user.svg",
        size: "medium",
        enabled: true,
        order: 1,
        bgColor: "rgba(156, 39, 176, 0.15)",
      },
      {
        id: "3",
        type: "photos",
        title: "الدورات",
        icon: "/img/widgets/photos.svg",
        size: "medium",
        enabled: true,
        order: 2,
        bgColor: "rgba(242, 70, 180, 0.15)",
      },
      {
        id: "4",
        type: "calendar",
        title: "التقويم",
        icon: "/img/widgets/calendar.svg",
        size: "medium",
        enabled: true,
        order: 3,
        bgColor: "rgba(242, 159, 70, 0.15)",
      },
      {
        id: "1",
        type: "mail",
        title: "البريد",
        icon: "/img/widgets/mail.svg",
        size: "small",
        enabled: true,
        order: 4,
        bgColor: "rgba(23, 92, 230, 0.15)",
      },
      {
        id: "6",
        type: "notes",
        title: "ملاحظات",
        icon: "/img/widgets/notes.svg",
        size: "small",
        enabled: true,
        order: 5,
        bgColor: "rgba(242, 225, 70, 0.15)",
      },
    ];

    // محاولة استرجاع تكوين الويدجت المخزن (إذا كان موجودًا)
    const savedWidgets = localStorage.getItem("user-widgets");
    if (savedWidgets) {
      try {
        // Parse stored widgets and make sure to filter any duplicate user widgets
        const parsedWidgets = JSON.parse(savedWidgets);

        // If there are multiple user widgets, keep only the first one
        const firstUserWidgetIndex = parsedWidgets.findIndex(
          (w) => w.type === "user"
        );
        const filteredWidgets = parsedWidgets.filter(
          (widget, index) =>
            widget.type !== "user" || index === firstUserWidgetIndex
        );

        setWidgets(filteredWidgets);
      } catch (error) {
        console.error("خطأ في تحميل الويدجت المخزنة:", error);
        setWidgets(defaultWidgets);
      }
    } else {
      setWidgets(defaultWidgets);
    }

    // استرجاع مخطط الألوان المحفوظ (إذا وجد)
    const savedColorScheme = localStorage.getItem("user-color-scheme");
    if (savedColorScheme) {
      setColorScheme(savedColorScheme);
    }

    // إعداد قائمة الويدجت المتاحة (لإضافة ويدجت جديدة)
    setAvailableWidgets(defaultWidgets);
  }, []);

  // حفظ التغييرات في التخزين المحلي
  useEffect(() => {
    if (widgets.length > 0) {
      localStorage.setItem("user-widgets", JSON.stringify(widgets));
    }
  }, [widgets]);

  // حفظ مخطط الألوان المحدد
  useEffect(() => {
    localStorage.setItem("user-color-scheme", colorScheme);
  }, [colorScheme]);

  // الحصول على مخطط الألوان الحالي
  const getCurrentColorScheme = () => {
    return (
      colorSchemes.find((scheme) => scheme.id === colorScheme) ||
      colorSchemes[5]
    ); // الأزرق هو الافتراضي
  };

  // التأكد من عدم وجود ويدجت مستخدم مكرر قبل الحفظ
  useEffect(() => {
    if (widgets.length > 0) {
      // Count user widgets
      const userWidgets = widgets.filter((widget) => widget.type === "user");

      // If there are multiple user widgets, keep only the first one
      if (userWidgets.length > 1) {
        console.log(
          "Removing duplicate user widgets before saving",
          userWidgets.length
        );
        const firstUserWidgetIndex = widgets.findIndex(
          (w) => w.type === "user"
        );

        setWidgets((prevWidgets) =>
          prevWidgets.filter(
            (widget, index) =>
              widget.type !== "user" || index === firstUserWidgetIndex
          )
        );
      }
    }
  }, [widgets]);

  // تغيير حجم الويدجت
  const changeWidgetSize = (widgetId: string, newSize: WidgetSize) => {
    setWidgets((currentWidgets) =>
      currentWidgets.map((widget) =>
        widget.id === widgetId ? { ...widget, size: newSize } : widget
      )
    );
  };

  // تغيير ترتيب الويدجت
  const onWidgetsReorder = (newOrder: WidgetInfo[]) => {
    // استبعاد ويدجت المستخدم من إعادة الترتيب
    const userWidget = widgets.find((widget) => widget.type === "user");

    // تحديث ترتيب الويدجت الأخرى فقط (newOrder هي بالفعل فقط الويدجت غير المستخدم)
    const updatedWidgets = newOrder.map((widget, index) => ({
      ...widget,
      order: index + 2, // يبدأ من 2 لأن ويدجت المستخدم سيبقى في المركز الأول
    }));

    // استرجاع كل الويدجت المعطلة
    const disabledWidgets = widgets.filter(
      (widget) => !widget.enabled && widget.type !== "user"
    );

    // دمج المصفوفة المحدثة مع ويدجت المستخدم والويدجت المعطلة
    const finalWidgets = [
      ...(userWidget ? [{ ...userWidget, order: 1 }] : []),
      ...updatedWidgets,
      ...disabledWidgets,
    ];

    setWidgets(finalWidgets);
  };

  // تمكين أو تعطيل ويدجت
  const toggleWidgetEnabled = (widgetId: string) => {
    setWidgets((currentWidgets) =>
      currentWidgets.map((widget) =>
        widget.id === widgetId
          ? { ...widget, enabled: !widget.enabled }
          : widget
      )
    );
  };

  // تغيير لون خلفية الويدجت
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const changeWidgetBgColor = (widgetId: string, newColor: string) => {
    setWidgets((currentWidgets) =>
      currentWidgets.map((widget) =>
        widget.id === widgetId ? { ...widget, bgColor: newColor } : widget
      )
    );
  };

  // فتح إعدادات الويدجت
  const openWidgetSettings = (widget: WidgetInfo) => {
    setSelectedWidget(widget);
    setShowSettings(true);
  };

  // إضافة ويدجت جديد (يمكن استخدامها لاحقًا)
  const addNewWidget = () => {
    // الكود المستقبلي لإضافة ويدجت جديد
    alert("سيتم تفعيل هذه الميزة قريبًا");
  };

  // التبديل بين عرض وإخفاء مختار الألوان
  const toggleColorSelector = (e: React.MouseEvent) => {
    e.stopPropagation(); // منع انتشار الحدث
    setShowColorSelector(!showColorSelector);
  };

  // تغيير مخطط الألوان
  const changeColorScheme = (schemeId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // منع انتشار الحدث
    setColorScheme(schemeId);
    setShowColorSelector(false);
  };

  // إغلاق مختار الألوان عند النقر خارجه
  useEffect(() => {
    const handleClickOutside = () => {
      if (showColorSelector) {
        setShowColorSelector(false);
      }
    };

    // إضافة المستمع للنقرات
    document.addEventListener("click", handleClickOutside);

    // إزالة المستمع عند تفكيك المكون
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showColorSelector]);

  // الويدجت النشطة (المفعلة)
  const activeWidgets = widgets
    .filter((widget) => widget.enabled)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen">
      {/* No background or navbar needed - GlobalLayout will handle it */}

      <div className="w-full max-w-7xl mx-auto pt-12 pb-20 px-4 md:px-8 overflow-hidden">
        {/* رأس الصفحة */}
        <div className="flex justify-between items-center mb-8 page-title-section">
          <div>
            <h1 className="text-3xl font-bold text-white mb-3">
              لوحة المعلومات
            </h1>
            <p className="text-white/70">
              مرحباً {user?.email?.split("@")[0] || "مستخدم"}، إليك أحدث
              المعلومات والتحديثات
            </p>
          </div>

          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            {/* أداة اختيار صور الخلفية */}
            <div className="relative">
              <button
                onClick={toggleColorSelector}
                className="py-1.5 px-3 mr-2 bg-black/20 hover:bg-black/30 text-white text-sm rounded-full flex items-center transition-all"
              >
                <div
                  className="w-4 h-4 rounded-full mr-2 ring-1 ring-white"
                  style={{
                    backgroundColor: getCurrentColorScheme().dotColor,
                  }}
                ></div>
                تغيير الخلفية
              </button>

              {/* قائمة اختيار الخلفيات */}
              <AnimatePresence>
                {showColorSelector && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 bg-black/40 backdrop-blur-lg rounded-xl shadow-xl p-4 z-50 min-w-[250px]"
                  >
                    <div className="flex flex-wrap justify-center gap-3">
                      {colorSchemes.map((scheme) => (
                        <button
                          key={scheme.id}
                          onClick={(e) => changeColorScheme(scheme.id, e)}
                          className={`w-10 h-10 rounded-full transition-all duration-200 bg-cover bg-center ${
                            colorScheme === scheme.id
                              ? "ring-2 ring-white scale-110"
                              : "ring-0 hover:ring-1 hover:ring-white/70"
                          }`}
                          style={{
                            backgroundImage: `url(/src/pages/page_bg/${scheme.id}BG.jpg)`,
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => setIsEditing(!isEditing)}
              className="px-4 py-2 bg-black/10 hover:bg-black/20 text-white rounded-full transition-all"
            >
              {isEditing ? "حفظ التغييرات" : "تخصيص الويدجت"}
            </button>
          </div>
        </div>

        {/* منطقة الويدجت */}
        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 py-8">
            {widgets.map((widget) => (
              <div
                key={widget.id}
                className={`
                  bg-black/20 backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-300
                  border border-white/10 shadow-lg relative
                  ${widget.enabled ? "" : "opacity-50"}
                `}
              >
                <div className="p-4 flex justify-between items-center">
                  <div className="flex items-center">
                    <div
                      className="w-10 h-10 flex items-center justify-center rounded-full mr-3"
                      style={{
                        backgroundColor:
                          widget.bgColor || "rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <img
                        src={widget.icon}
                        alt={widget.title}
                        className="w-6 h-6"
                      />
                    </div>
                    <span className="text-white font-semibold">
                      {widget.title}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <button
                      onClick={() => toggleWidgetEnabled(widget.id)}
                      className={`p-2 rounded-full ${
                        widget.enabled ? "text-green-400" : "text-gray-400"
                      }`}
                    >
                      {widget.enabled ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>

                    <button
                      onClick={() => openWidgetSettings(widget)}
                      className="p-2 text-blue-300 rounded-full"
                      title="إعدادات"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="p-4 border-t border-white/10">
                  <div className="flex space-x-2 rtl:space-x-reverse">
                    <button
                      onClick={() => changeWidgetSize(widget.id, "small")}
                      className={`px-3 py-1 rounded-full text-xs ${
                        widget.size === "small"
                          ? `bg-[${
                              getCurrentColorScheme().dotColor
                            }] text-white`
                          : "bg-black/30 text-white/70"
                      }`}
                      style={
                        widget.size === "small"
                          ? {
                              backgroundColor: getCurrentColorScheme().dotColor,
                            }
                          : {}
                      }
                    >
                      صغير
                    </button>
                    <button
                      onClick={() => changeWidgetSize(widget.id, "medium")}
                      className={`px-3 py-1 rounded-full text-xs ${
                        widget.size === "medium"
                          ? `bg-[${
                              getCurrentColorScheme().dotColor
                            }] text-white`
                          : "bg-black/30 text-white/70"
                      }`}
                      style={
                        widget.size === "medium"
                          ? {
                              backgroundColor: getCurrentColorScheme().dotColor,
                            }
                          : {}
                      }
                    >
                      متوسط
                    </button>
                    <button
                      onClick={() => changeWidgetSize(widget.id, "large")}
                      className={`px-3 py-1 rounded-full text-xs ${
                        widget.size === "large"
                          ? `bg-[${
                              getCurrentColorScheme().dotColor
                            }] text-white`
                          : "bg-black/30 text-white/70"
                      }`}
                      style={
                        widget.size === "large"
                          ? {
                              backgroundColor: getCurrentColorScheme().dotColor,
                            }
                          : {}
                      }
                    >
                      كبير
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* All widgets in a grid layout */}
            <Reorder.Group
              as="div"
              axis="y"
              values={activeWidgets}
              onReorder={onWidgetsReorder}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 py-4"
            >
              {/* Filter out duplicate user widgets, only keep the first one */}
              {activeWidgets
                .filter(
                  (widget, index, self) =>
                    widget.type !== "user" ||
                    index === self.findIndex((w) => w.type === "user")
                )
                .map((widget) => (
                  <Reorder.Item
                    key={widget.id}
                    value={widget}
                    as="div"
                    className={`widget-container ${
                      widget.size === "small"
                        ? "col-span-1"
                        : widget.size === "medium"
                        ? "col-span-2"
                        : widget.size === "large"
                        ? "col-span-3"
                        : widget.size === "wide"
                        ? "col-span-4"
                        : widget.size === "full"
                        ? "col-span-full"
                        : "col-span-1"
                    }`}
                    dragListener={widget.type !== "user"} // Only enable drag for non-user widgets
                    whileDrag={{
                      scale: 1.02,
                      opacity: 0.8,
                      boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
                    }}
                    dragControls={undefined}
                    dragElastic={0.1}
                  >
                    <Widget
                      widget={widget}
                      isEditing={isEditing}
                      onSizeChange={changeWidgetSize}
                      onEditClick={() => openWidgetSettings(widget)}
                      hideHeader={widget.type === "user"}
                    />
                  </Reorder.Item>
                ))}
            </Reorder.Group>
          </>
        )}

        {/* نافذة إعدادات الويدجت */}
        {showSettings && selectedWidget && (
          <WidgetSettings
            widget={selectedWidget}
            onClose={() => {
              setShowSettings(false);
              setSelectedWidget(null);
            }}
            onSave={(updatedWidget) => {
              setWidgets((currentWidgets) =>
                currentWidgets.map((w) =>
                  w.id === updatedWidget.id ? updatedWidget : w
                )
              );
              setShowSettings(false);
              setSelectedWidget(null);
            }}
          />
        )}
      </div>

      {/* تذييل الصفحة - سيتم عرضه من AdminLayout */}

      {/* No background overlay, just the clean background image */}
    </div>
  );
};

export default UserWidgetsDashboard;
